import { LoadRequestChat } from "@/components/LoadRequestChat";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function NewLoad() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const companyName = currentOrganization?.organization_name || "Your Company";
  const targetDashboard = (currentOrganization?.organization_type === 'customer') ? '/customer-dashboard' : '/carrier-dashboard';

  const handleLoadConfirmed = async (loadRequestData: any) => {
    if (!user) {
      throw new Error("You must be logged in to create load requests");
    }

    // Check if user has an organization
    const { data: userOrgs } = await supabase.rpc('get_user_organizations', { 
      user_uuid: user.id 
    });
    
    if (!userOrgs || userOrgs.length === 0) {
      throw new Error("Please create or join an organization first");
    }
    
    // Use the current organization if selected, otherwise the first one
    const orgToUse = currentOrganization?.organization_id || userOrgs[0].organization_id;
    
    // First create the load order
    const { data: loadOrder, error: orderError } = await supabase
      .from('load_orders')
      .insert({
        created_by: user.id,
        customer_org_id: orgToUse,
        title: loadRequestData.title || 'Load Order',
        description: loadRequestData.description || `Load order with ${loadRequestData.cargo_desc || 'cargo'}`,
        cargo_type: loadRequestData.cargo_type || 'general',
        cargo_desc: loadRequestData.cargo_desc,
        cargo_weight: loadRequestData.cargo_weight,
        cargo_value: null,
        vehicles_needed: loadRequestData.vehicles_needed || 1,
        vehicles_assigned: 0,
        vehicles_active: 0,
        vehicles_completed: 0,
        vehicles_canceled: 0,
        status: 'published'
      })
      .select()
      .single();

    if (orderError) throw orderError;

    // Create routes (multi-route or single route)
    if (loadRequestData.routes && Array.isArray(loadRequestData.routes)) {
      const routesToInsert = loadRequestData.routes.map((route: any) => ({
        load_order_id: loadOrder.id,
        customer_org_id: orgToUse,
        title: route.title,
        pickup_address: route.pickup_address,
        pickup_city: route.pickup_city,
        pickup_state: route.pickup_state,
        pickup_country: route.pickup_country || 'Saudi Arabia',
        pickup_date: route.pickup_date,
        delivery_address: route.delivery_address,
        delivery_city: route.delivery_city,
        delivery_state: route.delivery_state,
        delivery_country: route.delivery_country || 'Saudi Arabia',
        delivery_date: route.delivery_date,
        vehicle_type: route.vehicle_type || 'truck',
        vehicles_needed: route.vehicles_needed || 1,
        vehicles_assigned: 0,
        vehicles_active: 0,
        vehicles_completed: 0,
        vehicles_canceled: 0,
        status: 'published'
      }));

      const { error: routesError } = await supabase
        .from('load_routes')
        .insert(routesToInsert);

      if (routesError) throw routesError;
    } else {
      const { error: routeError } = await supabase
        .from('load_routes')
        .insert({
          load_order_id: loadOrder.id,
          customer_org_id: orgToUse,
          title: loadRequestData.title || 'Route 1',
          pickup_address: loadRequestData.pickup_address,
          pickup_city: loadRequestData.pickup_city,
          pickup_state: loadRequestData.pickup_state,
          pickup_country: loadRequestData.pickup_country || 'Saudi Arabia',
          pickup_date: loadRequestData.pickup_date,
          delivery_address: loadRequestData.delivery_address,
          delivery_city: loadRequestData.delivery_city,
          delivery_state: loadRequestData.delivery_state,
          delivery_country: loadRequestData.delivery_country || 'Saudi Arabia',
          delivery_date: loadRequestData.delivery_date,
          vehicle_type: loadRequestData.vehicle_type || 'truck',
          vehicles_needed: loadRequestData.vehicles_needed || 1,
          vehicles_assigned: 0,
          vehicles_active: 0,
          vehicles_completed: 0,
          vehicles_canceled: 0,
          status: 'published'
        });

      if (routeError) throw routeError;
    }

    // Notifications are handled by DB triggers
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 p-4">
        <Button variant="outline" onClick={() => navigate(targetDashboard)}>
          {t('dashboard.backToDashboard')}
        </Button>
      </div>
      <LoadRequestChat companyName={companyName} onLoadConfirmed={handleLoadConfirmed} />
    </div>
  );
}
